import type { Href } from 'expo-router';
import type { ReactNode } from 'react';
import { ScrollView } from 'react-native';

import { IntegrationBadge } from '@/components/integrations/integration-badge';
import { PageHeaderRow } from '@/components/page-header-row';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { INTEGRATION_PROVIDERS, type IntegrationOwnerType } from '@/lib/integrations/types';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

type IntegrationSettingsScreenProps = Readonly<{
  backHref?: Href;
  ownerType: IntegrationOwnerType;
  ownerQuery?: string;
  returnBasePath: string;
  header?: ReactNode;
  title: string;
  subtitle: string;
}>;

export function IntegrationSettingsScreen({
  backHref,
  ownerType,
  ownerQuery,
  returnBasePath,
  header,
  title,
  subtitle,
}: IntegrationSettingsScreenProps) {
  const calendarExportEnabled = useReleaseFeatureFlag('calendarExport') === true;
  const calendarSyncEnabled = useReleaseFeatureFlag('calendarSync') === true;
  const instagramEnabled = useReleaseFeatureFlag('instagramIntegration') === true;
  const stravaEnabled = useReleaseFeatureFlag('stravaIntegration') === true;
  const providers = INTEGRATION_PROVIDERS.filter((provider) =>
    ownerType === 'tenant' ? provider.supportsTenant : provider.supportsUser
  ).filter((provider) => {
    if (provider.provider === 'apple_calendar') return calendarExportEnabled;
    if (provider.provider === 'google_calendar') return calendarSyncEnabled;
    if (provider.provider === 'instagram') return instagramEnabled;
    if (provider.provider === 'strava') return stravaEnabled;
    return false;
  });
  const calendarProviders = providers.filter((provider) => provider.category === 'calendar');
  const socialProviders = providers.filter((provider) => provider.category === 'social');
  const fitnessProviders = providers.filter((provider) => provider.category === 'fitness');

  return (
    <ScrollView
      contentInsetAdjustmentBehavior="automatic"
      className="flex-1 bg-background"
      contentContainerClassName="gap-4 p-6"
    >
      {header ?? <PageHeaderRow backHref={backHref} subtitle={subtitle} title={title} />}

      {calendarProviders.length ? (
        <Card>
          <CardHeader>
            <CardTitle>Calendars</CardTitle>
            <CardDescription>
              Add runs to calendars without rebuilding a calendar app.
            </CardDescription>
          </CardHeader>
          <CardContent className="gap-3">
            {calendarProviders.map((provider) => (
              <IntegrationBadge
                key={provider.provider}
                provider={provider.provider}
                ownerQuery={ownerQuery}
                returnPath={returnBasePath}
              />
            ))}
          </CardContent>
        </Card>
      ) : null}

      {socialProviders.length ? (
        <Card>
          <CardHeader>
            <CardTitle>Social</CardTitle>
            <CardDescription>Connect social accounts used by members and clubs.</CardDescription>
          </CardHeader>
          <CardContent className="gap-3">
            {socialProviders.map((provider) => (
              <IntegrationBadge
                key={provider.provider}
                provider={provider.provider}
                ownerQuery={ownerQuery}
                returnPath={returnBasePath}
              />
            ))}
          </CardContent>
        </Card>
      ) : null}

      {fitnessProviders.length ? (
        <Card>
          <CardHeader>
            <CardTitle>Fitness</CardTitle>
            <CardDescription>
              Link personal fitness accounts without importing or sharing activity data.
            </CardDescription>
          </CardHeader>
          <CardContent className="gap-3">
            {fitnessProviders.map((provider) => (
              <IntegrationBadge
                key={provider.provider}
                provider={provider.provider}
                ownerQuery={ownerQuery}
                returnPath={returnBasePath}
              />
            ))}
          </CardContent>
        </Card>
      ) : null}
    </ScrollView>
  );
}
